import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckoutScreen } from './CheckoutScreen';
interface RoutineLoaderProps {
  petName: string;
}
const LOADING_MESSAGES = [{
  threshold: 0,
  text: 'Analizando respuestas...'
}, {
  threshold: 20,
  text: 'Detectando patrones de comportamiento...'
}, {
  threshold: 45,
  text: 'Construyendo rutina personalizada...'
}, {
  threshold: 75,
  text: 'Optimizando resultados...'
}, {
  threshold: 100,
  text: '¡Listo!'
}];
const PROGRESS_KEYFRAMES = [{
  time: 0,
  value: 0
}, {
  time: 200,
  value: 5
}, {
  time: 500,
  value: 15
}, {
  time: 800,
  value: 28
}, {
  time: 1100,
  value: 42
}, {
  time: 1400,
  value: 55
}, {
  time: 1700,
  value: 68
}, {
  time: 1900,
  value: 78
}, {
  time: 2100,
  value: 88
}, {
  time: 2300,
  value: 95
}, {
  time: 2500,
  value: 100
}];
export function RoutineLoader({
  petName
}: RoutineLoaderProps) {
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState<'loading' | 'pricing'>('loading');
  const animFrameRef = useRef<number>();
  const startTimeRef = useRef<number>(0);
  const name = petName || 'tu perro';
  useEffect(() => {
    if (phase !== 'loading') return;
    startTimeRef.current = performance.now();
    function animate(now: number) {
      const elapsed = now - startTimeRef.current;
      let currentValue = 0;
      for (let i = 0; i < PROGRESS_KEYFRAMES.length - 1; i++) {
        const current = PROGRESS_KEYFRAMES[i];
        const next = PROGRESS_KEYFRAMES[i + 1];
        if (elapsed >= current.time && elapsed <= next.time) {
          const segmentProgress = (elapsed - current.time) / (next.time - current.time);
          const eased = 1 - Math.pow(1 - segmentProgress, 3);
          currentValue = current.value + (next.value - current.value) * eased;
          break;
        } else if (elapsed > next.time) {
          currentValue = next.value;
        }
      }
      currentValue = Math.min(100, Math.round(currentValue));
      setProgress(currentValue);
      if (currentValue >= 100) {
        setTimeout(() => {
          setPhase('pricing');
        }, 600);
        return;
      }
      animFrameRef.current = requestAnimationFrame(animate);
    }
    animFrameRef.current = requestAnimationFrame(animate);
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [phase]);
  const currentMessage = [...LOADING_MESSAGES].reverse().find(m => progress >= m.threshold)?.text || LOADING_MESSAGES[0].text;
  return <div className="flex flex-col flex-1 items-center justify-center px-2" data-id="element-335">
      <AnimatePresence mode="wait" data-id="element-336">
        {/* PHASE 1: Loading */}
        {phase === 'loading' && <motion.div key="loading-phase" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="flex flex-col items-center w-full max-w-sm py-12" data-id="element-337">
            <motion.h2 initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: 'easeOut'
        }} className="text-[20px] font-extrabold text-stone-900 text-center leading-snug mb-10 px-2" data-id="element-338">
              Estamos creando la rutina personalizada de{' '}
              <span className="text-orange-600" data-id="element-339">{name}</span>...
            </motion.h2>

            <div className="w-full mb-5" data-id="element-340">
              <div className="w-full h-3 bg-stone-100 rounded-full overflow-hidden" data-id="element-341">
                <motion.div className="h-full rounded-full" style={{
              width: `${progress}%`,
              background: 'linear-gradient(90deg, #f97316, #ea580c)',
              transition: 'width 0.15s ease-out'
            }} data-id="element-342" />
              </div>
              <div className="flex justify-between mt-2" data-id="element-343">
                <span className="text-[12px] font-bold text-stone-400" data-id="element-344">
                  {progress}%
                </span>
              </div>
            </div>

            <AnimatePresence mode="wait" data-id="element-345">
              <motion.p key={currentMessage} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} transition={{
            duration: 0.25,
            ease: 'easeOut'
          }} className="text-[14px] font-semibold text-stone-500 text-center" data-id="element-346">
                {currentMessage}
              </motion.p>
            </AnimatePresence>

            <div className="flex gap-1.5 mt-8" data-id="element-347">
              {[0, 1, 2].map(i => <motion.div key={i} animate={{
            opacity: [0.3, 1, 0.3],
            scale: [0.8, 1, 0.8]
          }} transition={{
            duration: 1,
            repeat: Infinity,
            delay: i * 0.2,
            ease: 'easeInOut'
          }} className="w-1.5 h-1.5 rounded-full bg-orange-400" data-id="element-348" />)}
            </div>
          </motion.div>}

        {/* PHASE 2: Pricing Checkout */}
        {phase === 'pricing' && <motion.div key="pricing-phase" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} className="flex flex-col w-full flex-1 -mx-2" data-id="element-349">
            <CheckoutScreen petName={name} data-id="element-350" />
          </motion.div>}
      </AnimatePresence>
    </div>;
}